// Per-app reference status helpers, rollout texts and the bounded polling while an app rolls out (spec 6.10.19.3,
// feature spec 15). Nothing here branches on a platform name: the texts come from the delivery registry.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { SettingDelivery } from '../api/types';
import { formatClock, formatRelative } from '../lib/format';
import { strings } from '../strings';
import { isRollingOut } from './useVariablesData';
import type { VariableApp, VariableRow } from './model';
import { deliveryEntry, type DeliveryTypeDefinition } from './registry';

/** Every 30 s while an app is pending, for at most 10 minutes (spec 6.10.19.3 Bounded polling). */
export const POLL_INTERVAL_MS = 30_000;
export const POLL_LIMIT_MS = 10 * 60_000;

/** The app's delivery by reference (the first reference row with a rollout), if the app has one. */
export function appDelivery(
  app: Pick<VariableApp, 'resourceId'>,
  rows: readonly VariableRow[],
): { delivery: SettingDelivery; entry: DeliveryTypeDefinition } | null {
  const row = rows.find(
    (r) => r.resourceId === app.resourceId && r.delivery?.mode === 'reference' && r.delivery.nowAction,
  );
  return row?.delivery ? { delivery: row.delivery, entry: deliveryEntry(row.delivery) } : null;
}

export type AppStatusTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

/** The app status chip's words and tone. */
export function appStatusText(
  app: Pick<VariableApp, 'resourceId' | 'appReferenceStatus' | 'lastRestart'>,
  entry: DeliveryTypeDefinition | null,
  now = Date.now(),
): { label: string; tone: AppStatusTone; help: string | null } | null {
  const status = app.appReferenceStatus;
  if (!status) return null;
  if (isRollingOut(app) && entry?.rollingOutText)
    return {
      label: entry.rollingOutText(
        app.resourceId,
        formatRelative(app.lastRestart!.at, now),
        app.lastRestart!.by,
      ),
      tone: 'info',
      help: null,
    };
  const raw = entry?.rawStatusText?.[status.status];
  switch (status.neutral) {
    case 'resolved':
      return {
        label: entry?.statusText?.resolved ?? status.status,
        tone: 'success',
        help: entry?.statusText
          ? entry.statusText.resolvedHelp(
              app.resourceId,
              strings.common.dash,
              status.readAt ? formatRelative(status.readAt, now) : strings.common.dash,
            )
          : null,
      };
    case 'pending':
      return { label: entry?.statusText?.pending ?? status.status, tone: 'info', help: status.details };
    case 'access-denied':
    case 'not-found':
    case 'invalid':
      return {
        label: raw ?? strings.vaults.appNeutral[status.neutral] ?? status.status,
        tone: 'danger',
        help:
          [entry?.referenceFailureText?.(app.resourceId), status.details].filter(Boolean).join(' ') || null,
      };
    case 'unreachable':
      return { label: raw ?? strings.vaults.appNeutral.unreachable, tone: 'warning', help: status.details };
    default:
      return { label: raw ?? status.status, tone: raw ? 'warning' : 'neutral', help: status.details };
  }
}

/**
 * Bounded polling (an exception to "no polling"): while any app in scope is pending and the window is visible,
 * the deployment's variables are fetched again every 30 s, without `fresh`, for at most 10 minutes; paused while
 * the browser tab is hidden. `since` identifies the pending period (null: nothing pending); returns true once its
 * 10 minutes passed with an app still pending.
 */
export function useBoundedPolling(
  deploymentId: string | null,
  since: string | null,
  visible: boolean,
): boolean {
  const queryClient = useQueryClient();
  const [expired, setExpired] = useState<string | null>(null);
  useEffect(() => {
    if (since === null || !deploymentId || !visible) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - startedAt >= POLL_LIMIT_MS) {
        window.clearInterval(timer);
        setExpired(since);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['deployment', deploymentId, 'variables'] });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [deploymentId, since, visible, queryClient]);
  return since !== null && expired === since;
}

/** "Rolling out since 10:42" for a disabled rollout action. */
export function rollingOutSince(app: Pick<VariableApp, 'lastRestart'>): string {
  return strings.vaults.rollingOutSince(formatClock(app.lastRestart?.at ?? null));
}

/** "checked <n> ago · oldest task started <t> ago" (Cloud Run: "revision created <t> ago"). */
export function appStatusParts(
  app: VariableApp,
  delivery: SettingDelivery | null,
  checkedAt: string | null,
): string[] {
  const readAt = app.appReferenceStatus?.readAt ?? null;
  return [
    checkedAt ? strings.vaults.checked(formatRelative(checkedAt)) : null,
    readAt
      ? delivery?.platform === 'cloud-run'
        ? strings.vaults.revisionCreated(formatRelative(readAt))
        : strings.vaults.oldestTask(formatRelative(readAt))
      : null,
  ].filter((part): part is string => !!part);
}
